import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactApexChart from 'react-apexcharts';
import httpService from '../../../services/http.service';
import { ShimmerChart, ShimmerRows } from '../../Shared/Shimmer';
import KpiCard from '../../Shared/KpiCard';
import { PALETTE, formatInt, formatPercent, mergeCountryDistribution } from './statsUtils';

const TOP = 10;

const RankingCard = ({ title, subtitle, icon, rows, color, loading, emptyLabel, usersLabel }) => {
    const top = rows.slice(0, TOP);
    const chart = {
        series: [{ name: usersLabel, data: top.map((row) => row.users) }],
        options: {
            chart: { toolbar: { show: false }, fontFamily: 'inherit' },
            colors: [color],
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '62%' } },
            dataLabels: { enabled: false },
            grid: { borderColor: '#eef1f5', strokeDashArray: 4 },
            xaxis: { categories: top.map((row) => row.country), min: 0, labels: { formatter: (v) => Math.round(v) } },
            tooltip: { y: { formatter: (v) => formatInt(v) } },
        },
    };

    return (
        <div className="card stat-card">
            <div className="header">
                <h2><i className={`fa ${icon} mr-2 stats-card-icon`} aria-hidden="true" />{title}</h2>
                <small className="text-muted">{subtitle}</small>
            </div>
            <div className="body">
                {loading ? (
                    <>
                        <ShimmerChart height={240} />
                        <div className="mt-3"><ShimmerRows rows={4} /></div>
                    </>
                ) : top.length === 0 ? (
                    <div className="text-center text-muted py-5">{emptyLabel}</div>
                ) : (
                    <>
                        <ReactApexChart options={chart.options} series={chart.series} type="bar" height={Math.max(220, top.length * 34 + 40)} />
                        <ol className="rank-list">
                            {top.map((row, index) => (
                                <li key={row.country}>
                                    <span className="rank-list__rank">{index + 1}</span>
                                    <span className="rank-list__name">{row.country}</span>
                                    <span className="rank-list__bar" aria-hidden="true"><span style={{ width: `${Math.max(row.share, 2)}%`, background: color }} /></span>
                                    <span className="rank-list__value">{formatInt(row.users)}</span>
                                    <span className="rank-list__share">{formatPercent(row.share)}</span>
                                </li>
                            ))}
                        </ol>
                    </>
                )}
            </div>
        </div>
    );
};

/** Utilisateurs par pays : indicateurs clés, puis classement des pays les plus actifs et des inscriptions récentes. */
const UsersByCountry = () => {
    const { t } = useTranslation();
    const [state, setState] = useState({ loading: true, active: [], recent: [], failed: false });

    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([
            httpService.get('/stats/top-active-users-by-country'),
            httpService.get('/stats/top-recent-users-by-country'),
        ]).then(([active, recent]) => {
            if (cancelled) return;
            setState({
                loading: false,
                failed: active.status === 'rejected' && recent.status === 'rejected',
                active: active.status === 'fulfilled' ? mergeCountryDistribution(active.value?.data?.data || []) : [],
                recent: recent.status === 'fulfilled' ? mergeCountryDistribution(recent.value?.data?.country_distribution || []) : [],
            });
        });
        return () => { cancelled = true; };
    }, []);

    const kpis = useMemo(() => {
        const totalActive = state.active.reduce((sum, row) => sum + row.users, 0);
        const totalRecent = state.recent.reduce((sum, row) => sum + row.users, 0);
        const leader = state.active[0];
        return { totalActive, totalRecent, countries: state.active.length, leader };
    }, [state]);

    if (state.failed) {
        return <div className="alert alert-warning">{t('Impossible de charger ces statistiques.')}</div>;
    }

    return (
        <>
            <div className="row clearfix equal-height-row">
                <div className="col-xl-3 col-md-6 col-12">
                    <KpiCard
                        icon="fa-globe"
                        tone="blue"
                        label={t('Pays représentés')}
                        value={formatInt(kpis.countries)}
                        hint={t('{{n}} pays avec des inscriptions récentes', { n: state.recent.length })}
                        loading={state.loading}
                    />
                </div>
                <div className="col-xl-3 col-md-6 col-12">
                    <KpiCard
                        icon="fa-bolt"
                        tone="green"
                        label={t('Utilisateurs actifs')}
                        value={formatInt(kpis.totalActive)}
                        hint={kpis.countries > 0 ? t('{{n}} en moyenne par pays', { n: formatInt(Math.round(kpis.totalActive / kpis.countries)) }) : null}
                        loading={state.loading}
                    />
                </div>
                <div className="col-xl-3 col-md-6 col-12">
                    <KpiCard
                        icon="fa-user-plus"
                        tone="orange"
                        label={t('Inscriptions récentes')}
                        value={formatInt(kpis.totalRecent)}
                        hint={state.recent[0] ? t('{{country}} en tête', { country: state.recent[0].country }) : null}
                        loading={state.loading}
                    />
                </div>
                <div className="col-xl-3 col-md-6 col-12">
                    <KpiCard
                        icon="fa-trophy"
                        tone="purple"
                        label={t('Pays n°1')}
                        value={kpis.leader ? kpis.leader.country : '-'}
                        hint={kpis.leader ? `${formatPercent(kpis.leader.share)} ${t('des utilisateurs actifs')}` : null}
                        progress={kpis.leader ? kpis.leader.share : undefined}
                        loading={state.loading}
                    />
                </div>
            </div>

            <div className="row clearfix equal-height-row">
                <div className="col-xl-6 col-12">
                    <RankingCard
                        title={t("Pays avec le plus d'utilisateurs actifs")}
                        subtitle={t('Top {{n}}', { n: TOP })}
                        icon="fa-bolt"
                        rows={state.active}
                        color={PALETTE[0]}
                        loading={state.loading}
                        emptyLabel={t('Aucune donnée à afficher.')}
                        usersLabel={t('Utilisateurs actifs')}
                    />
                </div>
                <div className="col-xl-6 col-12">
                    <RankingCard
                        title={t("Pays avec le plus d'utilisateurs récents")}
                        subtitle={t('Top {{n}}', { n: TOP })}
                        icon="fa-user-plus"
                        rows={state.recent}
                        color={PALETTE[2]}
                        loading={state.loading}
                        emptyLabel={t('Aucune donnée à afficher.')}
                        usersLabel={t('Inscriptions récentes')}
                    />
                </div>
            </div>
        </>
    );
};

export default UsersByCountry;
